import { ContentCopy, Edit, Visibility } from '@mui/icons-material'
import { Box, IconButton, Typography } from '@mui/material'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import {
  MRT_Cell,
  MRT_Row,
  MRT_TableInstance,
  MRT_RowSelectionState as MRTRowSelectionState,
} from 'material-react-table'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import { useLoggedInUser } from '../../../../app/hooks/useLoggedInUser'
import { useNotificationStack } from '../../../../app/hooks/useNotificationStack'
import {
  useCopyShipmentMutation,
  useGetShipmentSummariesQuery,
} from '../../../../app/redux-fetch/apiOrders'
import AddButton from '../../../../components/Buttons/AddButton'
import { useConfirmDialogContext } from '../../../../components/ConfirmDialog'
import MRTDataGridv2 from '../../../../components/MRTDataGrid/MRTDataGridv2'
import { default as rootEnum, default as RoutesEnum } from '../../../../components/Routes/rootEnum'
import SwitchWithLabels from '../../../../components/Switches/SwitchWithLabels'
import { TableTitleTypography } from '../../../../components/Typographies/TableTitleTypography'
import { TruncatedText } from '../../../../components/Typographies/TruncatedText'
import { MRTColumnDef, OrderStatuses } from '../../../../types/common'
import messages from '../messages'
import { ShipmentDto } from '../types'

dayjs.extend(utc)

// Status ID mapping - these would typically come from the backend
const STATUS_ID_MAPPING = {
  Suspended: 100,
  Submitted: 3,
  Rejected: 151,
} as const

const ShipmentTable = () => {
  const { tenantId, customerId, isCustomerUser, isTenantUser } = useLoggedInUser()

  const { formatMessage, locale } = useIntl()

  const [includeUnsubmitted, setIncludeUnsubmitted] = useState(isCustomerUser)
  const [includeAbandoned, setIncludeAbandoned] = useState(false)
  const [includeCompleted, setIncludeCompleted] = useState(false)

  const { data: shipments = [], isLoading: shipmentsLoading, isFetching: shipmentsFetching } = useGetShipmentSummariesQuery({

    tenantId,
    customerId,
    includeUnsubmitted,
    includeAbandoned,
    includeCompleted,
  })

  const numberFormatter = useMemo(() => {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })
  }, [locale])

  const navigate = useNavigate()

  const handleNewShipment = useCallback(() => {
    navigate(rootEnum.SHIPMENT_STEPPER)
  }, [navigate])

  const [copyShipment] = useCopyShipmentMutation()
  const { enqueueSuccess, enqueueFailure } = useNotificationStack()
  const { confirm } = useConfirmDialogContext()

  const handleCopyShipment = useCallback(
    async (shipmentId: number) => {
      const confirmed = await confirm({
        title: formatMessage(messages.copyShipmentConfirmTitle),
        message: formatMessage(messages.copyShipmentConfirmMessage),
        confirmText: 'Yes',
        cancelText: 'No',
        severity: 'info',
        confirmButtonColor: 'primary',
        cancelButtonColor: 'secondary',
      })

      if (confirmed) {
        try {
          const response = await copyShipment({ tenantId, customerId, id: shipmentId }).unwrap()
          enqueueSuccess(messages.copyShipmentSuccess)
          navigate(`${RoutesEnum.EDIT_SHIPMENT.replace(':id', String(response.id))}`)
        } catch (error) {
          enqueueFailure(messages.copyShipmentFailure)
        }
      }
    },
    [
      confirm,
      formatMessage,
      copyShipment,
      tenantId,
      customerId,
      enqueueSuccess,
      enqueueFailure,
      navigate,
    ]
  )

  const shouldStatusBeRed = useCallback(
    (status: string) => {
      if (status === 'Suspended') {
        return true
      }
      if (isTenantUser && status === 'Submitted') {
        return true
      }

      if (isCustomerUser && status === 'Rejected') {
        return true
      }

      return false
    },
    [isTenantUser, isCustomerUser]
  )

  const createRowActions = ({
    cell,
    row,
    table,
    staticRowIndex,
  }: {
    cell: MRT_Cell<ShipmentDto, unknown>
    row: MRT_Row<ShipmentDto>
    table: MRT_TableInstance<ShipmentDto>
    staticRowIndex?: number
  }) => {
    const { status } = row.original
    const isEditable =
      status === OrderStatuses.Draft ||
      status === OrderStatuses.Rejected ||
      status === OrderStatuses.Created
    return (
      <Box sx={{ display: 'flex' }}>
        <IconButton
          color={'primary'}
          onClick={() =>
            navigate(`${RoutesEnum.VIEW_SHIPMENT.replace(':id', String(row.original.id))}`)
          }
        >
          <Visibility />
        </IconButton>
        <IconButton
          color={'primary'}
          onClick={() => handleCopyShipment(row.original.id!)}
          title={formatMessage(messages.copyShipment)}
        >
          <ContentCopy />
        </IconButton>
        {isEditable && (
          <IconButton
            color={'primary'}
            onClick={() =>
              navigate(`${RoutesEnum.EDIT_SHIPMENT.replace(':id', String(row.original.id))}`)
            }
          >
            <Edit />
          </IconButton>
        )}
      </Box>
    )
  }

  const columns = useMemo<MRTColumnDef<ShipmentDto>[]>(() => {
    return [
      {
        id: 'customerName',
        enableGlobalFilter: true,
        header: formatMessage(messages.customer),
        accessorFn: (row) => (row as any).customerName ?? '',
        Cell: ({ renderedCellValue }) => (
          <Typography sx={{ fontWeight: 400 }}>{renderedCellValue}</Typography>
        ),
      },
      {
        id: 'status',
        enableGlobalFilter: true,
        header: formatMessage(messages.status),
        accessorFn: (row) => row.status ?? '',
        Cell: ({ renderedCellValue }) => {
          const shouldBeRed = shouldStatusBeRed(renderedCellValue as string)
          return (
            <Typography
              sx={{
                fontWeight: 700,
                color: shouldBeRed ? 'error.main' : 'inherit',
              }}
            >
              {renderedCellValue}
            </Typography>
          )
        },
      },
      {
        id: 'lastStatusChangeDate',
        enableGlobalFilter: true,
        accessorFn: (row) => {
          const date = dayjs.utc(row.lastStatusChangeDate).local()
          return date.isValid() ? date.format('YYYY-MM-DD HH:mm') : ''
        },
        header: formatMessage(messages.lastStatusChangeDate),
        enableColumnFilter: true,
        Cell: ({ renderedCellValue }) => <Typography>{renderedCellValue}</Typography>,
      },
      {
        id: 'code',
        enableGlobalFilter: true,
        header: formatMessage(messages.shipmentTrackingNumber),
        accessorFn: (row) => row.code ?? '',
        Cell: ({ renderedCellValue }) => <Typography>{renderedCellValue}</Typography>,
      },
      {
        id: 'customerReferenceNumber',
        enableGlobalFilter: true,
        header: formatMessage(messages.customerReferenceNumber),
        accessorFn: (row) => row.customerReferenceNumber ?? '',
        Cell: ({ renderedCellValue }) => <Typography>{renderedCellValue}</Typography>,
      },
      {
        id: 'description',
        enableGlobalFilter: true,
        header: formatMessage(messages.description),
        accessorFn: (row) => row.description ?? '',
        Cell: ({ renderedCellValue }) => <TruncatedText text={renderedCellValue} maxLength={50} />,
      },
    ]
  }, [formatMessage, shipments])

  const renderMoneyValue = (value: number, iso3: string) => {
    return `${iso3} ${value?.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}`
  }

  const renderUnitValue = (value?: number) => {
    return value ? numberFormatter.format(value) : 'N/A'
  }

  const consignmentColumns = useMemo<MRTColumnDef<any>[]>(() => {
    return [
      {
        id: 'trackingNumber',
        enableGlobalFilter: true,
        header: `${formatMessage(messages.consignmentTrackingNumber)} #`,
        accessorFn: (row) => row.trackingNumber ?? '',
      },
      {
        id: 'shipFromName',
        enableGlobalFilter: true,
        header: formatMessage(messages.shipFrom),
        accessorFn: (row) => row.shipFromName ?? '',
      },
      {
        id: 'deliverToName',
        enableGlobalFilter: true,
        header: formatMessage(messages.deliverTo),
        accessorFn: (row) => row.deliverToName ?? '',
      },
      {
        id: 'shipper',
        enableGlobalFilter: true,
        header: formatMessage(messages.shipper),
        accessorFn: (row) => row.shipper ?? '',
        Cell: ({ cell }: any) => (
          <Typography sx={{ fontWeight: 400 }}>{cell.getValue() ?? 'N/A'}</Typography>
        ),
      },
      {
        id: 'transportMode',
        enableGlobalFilter: true,
        header: formatMessage(messages.transportMode),
        accessorFn: (row) => row.transportMode ?? '',
      },
      {
        accessorKey: 'loads',
        header: formatMessage(messages.loads),
        muiTableHeadCellProps: {
          align: 'center',
        },
        muiTableBodyCellProps: {
          align: 'center',
        },
        Cell: ({ row }: any) => (
          <div>
            {row.original.loads?.map((pkg: any, index: number) => (
              <Typography key={index}>{pkg.loadTypeName}</Typography>
            ))}
          </div>
        ),
      },
      {
        header: formatMessage(messages.quantity),
        muiTableBodyCellProps: {
          align: 'right',
        },
        muiTableHeadCellProps: {
          align: 'right',
        },
        Cell: ({ row }: any) => (
          <div>
            {row.original.loads?.map((pkg: any, index: number) => (
              <Typography key={index}>{pkg.quantity}</Typography>
            ))}
          </div>
        ),
      },
      {
        header: formatMessage(messages.columnWeight),
        muiTableBodyCellProps: {
          align: 'right',
        },
        muiTableHeadCellProps: {
          align: 'right',
        },
        Cell: ({ row }: any) => (
          <div>
            {row.original.loads?.map((pkg: any, index: number) => (
              <Typography key={index}>
                {renderUnitValue(pkg.weight)} {pkg.weightUnit ?? ''}
              </Typography>
            ))}
          </div>
        ),
      },
      {
        header: formatMessage(messages.temperatureControlled),
        muiTableBodyCellProps: {
          align: 'right',
        },
        muiTableHeadCellProps: {
          align: 'right',
        },
        Cell: ({ row }: any) => (
          <div>
            {row.original.loads?.map((load: any, index: number) =>
              load.temperatureControlled ? (
                <Typography key={index} sx={{ fontWeight: 700, color: '#0088A0DE' }}>
                  Yes
                </Typography>
              ) : (
                <Typography key={index} sx={{ fontWeight: 700 }}>
                  No
                </Typography>
              )
            )}
          </div>
        ),
      },
      {
        header: formatMessage(messages.cargoValue),
        muiTableBodyCellProps: {
          align: 'right',
        },
        muiTableHeadCellProps: {
          align: 'right',
        },
        Cell: ({ row }: any) => (
          <div>
            {row.original.loads?.map((pkg: any, index: number) => (
              <Typography key={index}>{renderMoneyValue(pkg.value, pkg.currency)}</Typography>
            ))}
          </div>
        ),
      },
    ]
  }, [formatMessage, shipments])

  const renderConsignmentTable = ({ row }: any) => {
    return (
      <MRTDataGridv2
        state={{ isLoading: false }}
        columns={consignmentColumns}
        data={row.original.shipmentBookingSummaries ?? []}
        getRowId={(row) => row.id as any}
        enableColumnActions={false}
        enableColumnFilters={false}
        enablePagination={false}
        enableSorting={false}
        enableBottomToolbar={false}
        enableTopToolbar={false}
      />
    )
  }

  const [rowSelection, setRowSelection] = useState<MRTRowSelectionState>({})
  const [selectedShipments, setSelectedShipments] = useState<ShipmentDto[]>([])

  useEffect(() => {
    const selectedRowIds = Object.keys(rowSelection)
    const selectedRows = shipments.filter((shipment: any) =>
      selectedRowIds.includes(String(shipment.id))
    )
    setSelectedShipments(selectedRows)
  }, [rowSelection, shipments])

  return (
    <>
      <MRTDataGridv2
        initialState={{
          pagination: { pageSize: 100, pageIndex: 0 },
          density: 'comfortable',
          sorting: [
            {
              id: 'status',
              desc: false,
            },
          ],
        }}
        state={{
          isLoading: shipmentsLoading || shipmentsFetching,
          rowSelection: rowSelection,
        }}
        leftHeadingComponent={
          <Box sx={{ display: 'inline-flex' }}>
            <TableTitleTypography>{formatMessage(messages.shipments)}</TableTitleTypography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', marginLeft: '20px' }}>
              <SwitchWithLabels
                value={includeUnsubmitted}
                onChange={setIncludeUnsubmitted}
                labelBefore=""
                labelAfter={isCustomerUser ? formatMessage(messages.includeUnsubmitted) : formatMessage(messages.includeUnsubmittedRejected)}
              />
              <SwitchWithLabels
                value={includeAbandoned}
                onChange={setIncludeAbandoned}
                labelBefore=""
                labelAfter={formatMessage(messages.includeAbandoned)}
              />
              <SwitchWithLabels
                value={includeCompleted}
                onChange={setIncludeCompleted}
                labelBefore=""
                labelAfter={formatMessage(messages.includeCompletedArchived)}
              />
            </Box>
          </Box>
        }
        rightHeadingComponent={
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <AddButton text={formatMessage(messages.newShipment)} onClick={handleNewShipment} />
          </Box>
        }
        columns={columns}
        data={shipments}
        enableEditing
        enableRowActions
        enableExpanding
        enableRowSelection={false}
        onRowSelectionChange={setRowSelection}
        positionActionsColumn='last'
        enableColumnPinning={true}
        renderRowActions={createRowActions}
        renderDetailPanel={renderConsignmentTable}
      />
    </>
  )
}

export default ShipmentTable
